/*
 * Tooltip — tiny hover-only primitive, brand-styled.
 *
 * Pure React + CSS, no portal, no library dependency. Suitable for short
 * status text + the kind of "Schedule session / Upgrade to schedule" CTA
 * surfaces Phase 4 needs (sidebar consultant row, team page member row,
 * tier pill upgrade hint). Not a full popover system — for richer
 * content use the existing Dropdown / Modal primitives.
 *
 *   <Tooltip content="Schedule a session">
 *     <button>...</button>
 *   </Tooltip>
 *
 * The wrapped child becomes the trigger; the tooltip floats above on
 * hover or focus and renders centered. `side` controls placement.
 */

import React, { useId, useState } from 'react';

import './tooltip.css';


export interface TooltipProps {
  /** What pops on hover. Plain text or any inline React node. */
  content: React.ReactNode;
  /** The trigger. Must be a single React element. */
  children: React.ReactElement;
  /** Which side of the trigger to float above. Defaults to 'top'. */
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** Skip rendering entirely when content is empty/null — saves a wrapper. */
  className?: string;
}


export function Tooltip({
  content,
  children,
  side = 'top',
  className = '',
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const id = useId();

  if (content == null || content === '') return children;

  return (
    <span
      className={`aa-tooltip-wrap ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {React.cloneElement(children, {
        'aria-describedby': open ? id : undefined,
      })}
      {open && (
        <span
          id={id}
          role="tooltip"
          className={`aa-tooltip aa-tooltip--${side}`}
        >
          {content}
        </span>
      )}
    </span>
  );
}

export default Tooltip;
